import React, {FC, ReactNode} from "react";

interface ITextNode {
  type: "text";
  text: string;
  bold?: boolean;
  italic?: boolean;
  underline?: boolean;
  strikethrough?: boolean;
  code?: boolean;
}

interface ILinkNode {
  type: "link";
  url: string;
  children: IRichTextChild[];
}

interface IListItemNode {
  type: "list-item";
  children: IRichTextChild[];
}

interface IParagraphBlock {
  type: "paragraph";
  children: IRichTextChild[];
}

interface IHeadingBlock {
  type: "heading";
  level: 1 | 2 | 3 | 4 | 5 | 6;
  children: IRichTextChild[];
}

interface IListBlock {
  type: "list";
  format: "ordered" | "unordered";
  children: IListItemNode[];
}

type IRichTextChild = ITextNode | ILinkNode;
type IRichTextBlock = IParagraphBlock | IHeadingBlock | IListBlock;

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type IRichTextContent = IRichTextBlock[] | any[] | string | null | undefined;

interface IProps {
  content: IRichTextContent;
  className?: string;
  linkColor?: string;
}

const TEXT_FORMATTERS: {key: keyof ITextNode; tag: keyof JSX.IntrinsicElements}[] = [
  {key: "bold", tag: "strong"},
  {key: "italic", tag: "em"},
  {key: "underline", tag: "u"},
  {key: "strikethrough", tag: "s"},
];

const HEADING_SIZES: Record<number, string> = {
  1: "1.5em",
  2: "1.3em",
  3: "1.1em",
  4: "1em",
  5: "0.9em",
  6: "0.8em",
};

/**
 * Рендерит текстовый узел с форматированием
 */
const renderTextNode = (node: ITextNode, key: number): ReactNode => {
  if (!node.text) return null;

  let content: ReactNode = node.text;

  if (node.code) {
    content = (
      <code
        style={{
          backgroundColor: "rgba(255, 255, 255, 0.1)",
          padding: "2px 6px",
          borderRadius: "4px",
          fontFamily: "monospace",
        }}
      >
        {content}
      </code>
    );
  }

  TEXT_FORMATTERS.forEach(({key: formatKey, tag}) => {
    if (node[formatKey]) {
      content = React.createElement(tag, null, content);
    }
  });

  return <React.Fragment key={key}>{content}</React.Fragment>;
};

/**
 * Рендерит дочерние элементы (текст, ссылки)
 */
const renderChildren = (children: IRichTextChild[], linkColor: string): ReactNode[] => {
  return children.map((child, index) => {
    if (child.type === "link") {
      return (
        <a
          key={index}
          href={child.url}
          target="_blank"
          rel="noopener noreferrer"
          style={{color: linkColor, textDecoration: "underline"}}
        >
          {renderChildren(child.children, linkColor)}
        </a>
      );
    }

    return renderTextNode(child as ITextNode, index);
  });
};

/**
 * Рендерит элементы списка
 */
const renderListItems = (items: IListItemNode[], linkColor: string): ReactNode[] => {
  return items.map((item, index) => (
    <li key={index} style={{margin: "2px 0"}}>
      {renderChildren(item.children, linkColor)}
    </li>
  ));
};

/**
 * Рендерит блок контента (параграф, заголовок, список)
 */
const renderBlock = (block: IRichTextBlock, index: number, linkColor: string): ReactNode => {
  switch (block.type) {
    case "paragraph": {
      // Пропускаем пустые параграфы
      const isEmpty =
        block.children.length === 1 &&
        block.children[0].type === "text" &&
        !(block.children[0] as ITextNode).text;

      if (isEmpty) {
        return null;
      }

      return (
        <p key={index} style={{margin: 0}}>
          {renderChildren(block.children, linkColor)}
        </p>
      );
    }

    case "heading": {
      const HeadingTag = `h${block.level}` as keyof JSX.IntrinsicElements;

      return (
        <HeadingTag
          key={index}
          style={{
            fontSize: HEADING_SIZES[block.level] || "1em",
            fontWeight: 600,
            margin: "8px 0 4px 0",
          }}
        >
          {renderChildren(block.children, linkColor)}
        </HeadingTag>
      );
    }

    case "list": {
      const ListTag = block.format === "ordered" ? "ol" : "ul";

      return (
        <ListTag key={index} style={{margin: "4px 0", paddingLeft: "20px"}}>
          {renderListItems(block.children, linkColor)}
        </ListTag>
      );
    }

    default:
      return null;
  }
};

/**
 * Компонент для парсинга и отображения Strapi rich text контента
 */
export const RichTextParser: FC<IProps> = ({content, className, linkColor = "#56B3FF"}) => {
  if (typeof content === "string") {
    return <div className={className}>{content}</div>;
  }
  if (!Array.isArray(content) || content.length === 0) {
    return null;
  }

  return (
    <div className={className}>
      {content.map((block, index) => renderBlock(block, index, linkColor))}
    </div>
  );
};

/**
 * Функция для преобразования rich text в plain text (без форматирования)
 */
export const richTextToPlainText = (content: IRichTextContent): string => {
  if (typeof content === "string") {
    return content;
  }

  if (!Array.isArray(content)) {
    return "";
  }

  const extractText = (children: IRichTextChild[]): string => {
    return children
      .map((child) => {
        if (child.type === "text") {
          return child.text || "";
        }
        if (child.type === "link") {
          return extractText(child.children);
        }
        return "";
      })
      .join("");
  };

  return content
    .map((block) => {
      if (block.type === "list") {
        return block.children
          .map((item: IListItemNode) => extractText(item.children))
          .join("\n");
      }
      if ("children" in block) {
        return extractText(block.children);
      }
      return "";
    })
    .join("\n")
    .trim();
};

export default RichTextParser;
